import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  ActividadEconomica,
  ActividadEconomicaFiltro,
  ActividadEconomicaPageResponse
} from '../../../domain/model/actividad-economica/actividad-economica';
import { ActividadEconomicaRepository } from '../../../domain/service/actividad-economica/actividad-economica-repository';
import { validarSize } from 'src/app/common/validation-utils';

/**
 * Adaptador HTTP: HttpActividadEconomicaRepository
 *
 * Implementa el puerto ActividadEconomicaRepository para comunicación HTTP con backend.
 * Paginación keyset/cursor (skill acceso-datos-oracle §7 — nunca OFFSET).
 *
 * Endpoints:
 * - GET /api/servicios/actividades-economicas/search?codigo=&nombre=&cursor=&direction=&size=
 * - GET /api/servicios/actividades-economicas/{codigo}
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Injectable({ providedIn: 'root' })
export class HttpActividadEconomicaRepository implements ActividadEconomicaRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/actividades-economicas`;

  /**
   * Busca actividades económicas con filtros y paginación keyset/cursor.
   */
  search(
    filtro: ActividadEconomicaFiltro,
    cursor: string | null,
    size = 10,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST' = 'NEXT'
  ): Promise<ActividadEconomicaPageResponse> {
    validarSize(size);
    let params = new HttpParams()
      .set('size', size.toString())
      .set('direction', direction);

    if (filtro.codigo?.trim()) {
      params = params.set('codigo', filtro.codigo.trim());
    }
    if (filtro.nombre?.trim()) {
      params = params.set('nombre', filtro.nombre.trim());
    }
    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return firstValueFrom(
      this.http.get<ActividadEconomicaPageResponse>(`${this.baseUrl}/search`, { params })
    );
  }

  /**
   * Obtiene una actividad económica por código CIIU
   */
  getByCodigo(codigo: string): Promise<ActividadEconomica> {
    return firstValueFrom(
      this.http.get<ActividadEconomica>(`${this.baseUrl}/${codigo}`)
    );
  }
}
